import React from "react";
import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { BlackListClientsRowObj } from "..";
import DivLoader from "components/divloader/DivLoader";
import DeleteModal from "./DeleteBlockRecordModal";
import CreateBlockClientsModal from "./CreateBlockeclientModal";
import { deleteWhiteListRecord } from "api/whiteListed";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import toast from "react-hot-toast";

function WhiteList(props: {
  pageSize: any;
  setPageSize: any;
  page: any;
  setPage: any;
  totalpage: any;
  totalItems: any;
  currentPage: any;
  roleData: any;
  tableData: any;
  fetchBlackListClientsData: any;
  isLoading: boolean;
  setIsLoading: any;
  onValueChange?: any;
}) {
  const { pageSize, roleData, tableData, fetchBlackListClientsData, setIsLoading, onValueChange } = props;
  const [{ pageIndex }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize });
  const [data, setData] = React.useState(() => [...tableData]);
  const [searchVal, setSearchVal] = React.useState<any>("");
  const [isDeleteLoading, setIsDeleteLoading] = React.useState<boolean>(false);

  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  React.useEffect(() => { setData(tableData); }, [tableData]);

  const hasActions =
    roleData?.[0]?.whitelist?.value?.edit_whitelist ||
    roleData?.[0]?.whitelist?.value?.delete_whitelist;

  const handleDelete = (deleteId: string) => {
    setIsDeleteLoading(true);
    deleteWhiteListRecord(deleteId)
      .then((data) => { fetchBlackListClientsData(); toast.success(data?.message || "Record deleted"); })
      .catch((err) => toast.error(err?.response?.data?.message ?? "Record deletion failed"))
      .finally(() => setIsDeleteLoading(false));
  };

  const columns = [
    columnHelper.accessor(
      (row) => `${row.bin || ""}${row.last_four ? "-" + row.last_four : ""}`,
      {
        id: "binLastFour",
        header: () => <span className="bl-th">Bin + Last 4</span>,
        sortDescFirst: true,
        cell: (info) => (
          !info.row.getIsExpanded() && (
            <span className="bl-mono">{info.getValue() || <span className="bl-empty">—</span>}</span>
          )
        ),
      }
    ),
    columnHelper.accessor("id", {
      id: "name",
      header: () => <span className="bl-th">Name</span>,
      sortDescFirst: true,
      cell: (info) => (
        !info.row.getIsExpanded() && (
          <span className="bl-cell">
            {info?.row?.original?.first_name
              ? `${info.row.original.first_name} ${info.row.original.last_name || ""}`.trim()
              : <span className="bl-empty">—</span>}
          </span>
        )
      ),
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => <span className="bl-th">Email</span>,
      sortDescFirst: true,
      cell: (info) => (
        !info.row.getIsExpanded() && (
          <span className="bl-cell">{info.getValue() || <span className="bl-empty">—</span>}</span>
        )
      ),
    }),
    ...(hasActions ? [
      columnHelper.accessor("id", {
        id: "actions",
        header: () => <span className="bl-th">Actions</span>,
        enableSorting: false,
        cell: (info) => (
          !info.row.getIsExpanded() && (
            <span className="bl-actions">
              {roleData?.[0]?.whitelist?.value?.edit_whitelist && (
                <CreateBlockClientsModal
                  fetchBlackListClientsData={fetchBlackListClientsData}
                  id={info.getValue()}
                  roleData={roleData}
                  info={false}
                  infoData={info}
                />
              )}
              {roleData?.[0]?.whitelist?.value?.delete_whitelist && (
                <DeleteModal
                  fetchListData={fetchBlackListClientsData}
                  id={info.getValue()}
                  name={info?.row?.original?.first_name ?? ""}
                  heading="Delete Record"
                  message={`This will permanently delete ${info?.row?.original?.first_name || info?.row?.original?.id}. This action cannot be undone.`}
                  handleDelete={handleDelete}
                  isDeleteLoading={isDeleteLoading}
                />
              )}
            </span>
          )
        ),
      }),
    ] : []),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const handleValueChange = (e: any) => { onValueChange(e); setSearchVal(e); };

  return (
    <div className="bl-root">
      <div className="bl-card">
        {props?.isLoading ? (
          <div className="bl-loading">
            <div className="bl-spinner" />
            <p className="bl-loading-txt">Loading records…</p>
          </div>
        ) : (
          <div className="bl-table-wrap">
            <table className="bl-table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="bl-thead-row">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        onClick={header.column.getToggleSortingHandler()}
                        className={`bl-thead-cell${header.column.getCanSort() ? " bl-sortable" : ""}`}
                      >
                        <div className="bl-thead-inner">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="bl-sort-icon">
                              {header.column.getIsSorted() === "asc" ? (
                                <BsArrowUp size={12} />
                              ) : header.column.getIsSorted() === "desc" ? (
                                <BsArrowDown size={12} />
                              ) : (
                                <RiArrowUpDownFill size={12} />
                              )}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows?.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="bl-row">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="bl-td">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="bl-empty-row">
                      <div className="bl-empty-state">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/>
                        </svg>
                        <p>No whitelist records found.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <BlStyles />
    </div>
  );
}

export default WhiteList;
const columnHelper = createColumnHelper<BlackListClientsRowObj>();

const BlStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

    .bl-root { font-family: 'Plus Jakarta Sans', sans-serif; }

    .bl-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 60px 20px; }
    .bl-spinner { width: 28px; height: 28px; border: 3px solid #e2e8f0; border-top-color: #2563eb; border-radius: 50%; animation: blSpin .7s linear infinite; }
    @keyframes blSpin { to { transform: rotate(360deg); } }
    .bl-loading-txt { font-size: 13px; color: #94a3b8; font-weight: 500; }

    .bl-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 1px 4px rgba(15,23,42,.05); }
    .bl-table-wrap { overflow-x: auto; }
    .bl-table { width: 100%; border-collapse: collapse; font-size: 13px; }

    .bl-thead-row { background: #fafbfc; border-bottom: 1.5px solid #e2e8f0; }
    .bl-thead-cell { padding: 12px 16px; text-align: left; font-weight: 700; white-space: nowrap; }
    .bl-thead-cell.bl-sortable { cursor: pointer; user-select: none; }
    .bl-thead-cell.bl-sortable:hover { background: #f1f5f9; }
    .bl-thead-inner { display: flex; align-items: center; gap: 6px; }
    .bl-th { font-size: 11.5px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .4px; }
    .bl-sort-icon { color: #94a3b8; display: flex; align-items: center; }

    .bl-row { border-bottom: 1px solid #f1f5f9; transition: background .12s; }
    .bl-row:hover { background: #f8fafc; }
    .bl-row:last-child { border-bottom: none; }
    .bl-td { padding: 13px 16px; vertical-align: middle; }

    .bl-cell { font-size: 13px; font-weight: 600; color: #0f172a; }
    .bl-mono { font-family: 'Courier New', monospace; font-size: 12.5px; font-weight: 600; color: #1e293b; background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 4px 10px; display: inline-block; letter-spacing: .3px; }
    .bl-empty { font-size: 12.5px; color: #cbd5e1; font-style: italic; font-weight: 500; }

    .bl-actions { display: flex; align-items: center; gap: 8px; }

    .bl-empty-row { }
    .bl-empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 40px 20px; }
    .bl-empty-state p { font-size: 13px; color: #94a3b8; font-weight: 500; }
  `}</style>
);